/**
 * voice-v8 — Prozess-Freigabe: was Karte (frontend/lib/karten/prozess.ts) und Server (server/prozess-freigabe.ts) gemeinsam wissen.
 * (27.10.2026, Martins Knopf-Ja k-4d6ba4: „damit nicht jedes Mal bei einer Kleinstunterbrechung dieser Dreck einfach aufhört“)
 *
 * Nur feste Wörter und die Kennung der Karte (vom Server vergeben) — nie freier Text. Das Knopf-Ja ist der EINZIGE Weg zur Freigabe:
 * der Server vergleicht Martins Nachricht WORTGLEICH mit `prozessKnopfJa(<Kennung der offenen Karte>)`. Ein gesprochenes oder getipptes
 * „Ja“ trifft das nie (Kennung aus Zufallsbytes, Ziffern und Klammern).
 *
 * Mandat (27.10.2026, Martins Grundentscheidung Autonomie-Modus): EINE Karte VOR dem Start — Aufgabe (Martins eigener Wortlaut, vom
 * Server), feste Aufgabenart, drei Stufen (20 Min / 1 Std / ganzer Tag) mit Kostendeckel 3 / 8 / 25 €. Je Stufe ein eigener Knopf mit
 * festem Wortlaut: Stufe, Kennung und Kurzwert (HMAC des Servers über den Rahmen, server/mandat-rahmen.ts). Rahmen, Hosts und
 * Ausschlussliste kennt nur der Server.
 */
import { VORSCHLAG_KENNUNG } from "./training.js";

/** Form der Kennung einer Prozess-Karte (6 Zufallsbytes als Hex — wie ein Vorschlag). */
export const PROZESS_KENNUNG = VORSCHLAG_KENNUNG;

/** Der Name, unter dem die Karte als Ergebnis im Zug steht. KEIN Werkzeug: das Modell kann es nicht aufrufen. */
export const PROZESS_KARTE = "prozess_freigabe";

/** Die Frage der Karte — fest. */
export const PROZESS_FRAGE =
  "Soll ich den Prozess bis zum Ende laufen lassen (bis zu 10 Minuten)? Absenden, Hochladen, Bezahlen und Privates frage ich weiter einzeln.";

/** Der feste Wortlaut des Ja-Knopfs — EINE Stelle für Karte und Prüfung. Beginnt bewusst nicht mit „Ja“ (kein Ja für ein anderes Tor). */
export function prozessKnopfJa(kennung: string): string {
  return `Prozess bis zum Ende laufen lassen (Freigabe ${kennung})`;
}

/** Der Nein-Knopf derselben Karte (verwirft nur). */
export function prozessKnopfNein(kennung: string): string {
  return `Prozess nicht weiterlaufen lassen (Freigabe ${kennung})`;
}

// ---------------------------------------------------------------------------------------------------------------------------------
// Mandat
// ---------------------------------------------------------------------------------------------------------------------------------

/** Das Kern-Werkzeug, mit dem das Modell eine Mandats-Karte ANFRAGT (nur die Art-Kennung). Die Karte baut der Server. */
export const MANDAT_KARTE = "mandat_anfragen";

/** Form des Kurzwerts im Knopf-Wortlaut (8 Hex-Zeichen eines HMAC). */
export const MANDAT_KURZWERT = /^[0-9a-f]{8}$/;

/** Die festen Aufgabenarten — die Kennung, die das Modell nennen darf. Ihre Hosts stehen nur am Server (server/mandat-rahmen.ts). */
export const MANDAT_ARTEN = ["aws-ses", "webseite", "video-test", "backup", "wachwort-quote", "allgemein-browser", "soniox-einrichtung"] as const;
export type MandatArt = (typeof MANDAT_ARTEN)[number];

/** Was die Karte über die Art sagt — fest, kurz, einfach. */
export const MANDAT_ART_TITEL: Readonly<Record<MandatArt, string>> = Object.freeze({
  "aws-ses": "Amazon SES einrichten",
  webseite: "Arbeit an der Webseite",
  "video-test": "Video testen",
  backup: "Sicherung",
  "wachwort-quote": "Wachwort-Quote messen",
  "allgemein-browser": "Im Web nachsehen",
  "soniox-einrichtung": "Soniox einrichten",
});

export const MANDAT_STUFEN = ["20min", "1std", "tag"] as const;
export type MandatStufe = (typeof MANDAT_STUFEN)[number];

export interface StufenWerte {
  /** Wie die Karte und die Statuszeile die Stufe nennen. */
  titel: string;
  /** Dauer ab dem Tipp (ms) — absolut, nie verlängert. */
  ms: number;
  /** Harte Rundengrenze je Lauf. */
  runden: number;
  /** Kostendeckel in Euro-Cent. */
  euroCent: number;
}

/** Die drei Stufen — Martins Grundentscheidung (20 Min / 1 Std / ganzer Tag, 3 / 8 / 25 €). „Ganzer Tag“ = 12 Stunden ab dem Tipp. */
export const MANDAT_STUFEN_WERTE: Readonly<Record<MandatStufe, Readonly<StufenWerte>>> = Object.freeze({
  "20min": Object.freeze({ titel: "20 Min", ms: 20 * 60_000, runden: 60, euroCent: 300 }),
  "1std": Object.freeze({ titel: "1 Std", ms: 60 * 60_000, runden: 200, euroCent: 800 }),
  tag: Object.freeze({ titel: "Ganzer Tag", ms: 12 * 60 * 60_000, runden: 600, euroCent: 2500 }),
});

/** Was immer ein eigener Knopf bleibt — die Karte sagt es wörtlich. */
export const MANDAT_IMMER_EIGENER_KNOPF = "Geld, Zugangsdaten, Löschen, IAM-Benutzer anlegen, Formulare absenden und Mails an Dritte";

export function istMandatArt(v: unknown): v is MandatArt {
  return typeof v === "string" && (MANDAT_ARTEN as readonly string[]).includes(v);
}

export function istMandatStufe(v: unknown): v is MandatStufe {
  return typeof v === "string" && (MANDAT_STUFEN as readonly string[]).includes(v);
}

/** Der feste Wortlaut eines Stufen-Knopfs — Stufe, Kennung und Kurzwert. Beginnt nicht mit „Ja“. */
export function mandatKnopfJa(stufe: MandatStufe, kennung: string, kurzwert: string): string {
  return `Mandat ${MANDAT_STUFEN_WERTE[stufe].titel} starten (Mandat ${kennung}-${kurzwert})`;
}

/** Der Nein-Knopf der Mandats-Karte (verwirft nur). */
export function mandatKnopfNein(kennung: string): string {
  return `Kein Mandat (Mandat ${kennung})`;
}

/** Der Ja-Knopf der Halt-Karte: das angehaltene Mandat läuft weiter — mit demselben Ende, demselben Rahmen. */
export function mandatWeiterJa(kennung: string): string {
  return `Mandat fortsetzen (Mandat ${kennung})`;
}

/** Der Nein-Knopf der Halt-Karte: das Mandat endet. */
export function mandatWeiterNein(kennung: string): string {
  return `Mandat beenden (Mandat ${kennung})`;
}

/**
 * Limit-Karte (27.10.2026, B6): der dritte Knopf nach Tageslimit oder Kostendeckel — das Mandat endet heute; morgen geht es nur mit
 * einem NEUEN Tipp auf eine neue Mandats-Karte weiter. „Weiter“ und „Beenden“ sind die Knöpfe der Halt-Karte (oben).
 */
export function mandatMorgen(kennung: string): string {
  return `Mandat morgen weitermachen (Mandat ${kennung})`;
}

/** Warum die Limit-Karte kommt: das Tageslimit der Einstellungen oder der Kostendeckel des Mandats. */
/**
 * „schritte“, „abo-fenster“ und „abo-fenster-unbekannt“ (Stufe „tag“ ohne frischen Fensterwert, Prüfbefund M2): die Ersatz-Deckel des
 * Abo-Wegs (server/mandat-abo.ts).
 */
export type MandatLimitGrund = "tageslimit" | "budget" | "schritte" | "abo-fenster" | "abo-fenster-unbekannt";
